import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, RotateCcw, Headphones, Volume2, Waves, CloudRain, Wind, VolumeX } from "lucide-react";
import { getAudioContext, Ambience } from "../lib/audioEngine";

const NATURE = [
  { id: "off", label: "Off", icon: VolumeX },
  { id: "ocean", label: "Ocean", icon: Waves },
  { id: "rain", label: "Rain", icon: CloudRain },
  { id: "wind", label: "Wind", icon: Wind },
];

// In-browser guided meditation player using the Web Speech API (SpeechSynthesis).
// Reads the provided script lines aloud with a calm, slow voice. No external audio files.
export default function GuidedAudioPlayer({ label = "Guided practice", lines = [] }) {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window && lines.length > 0;

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(-1);
  const [nature, setNature] = useState("off");
  const voiceRef = useRef(null);
  const idxRef = useRef(0);
  const cancelledRef = useRef(false);
  const ambienceRef = useRef(null);
  const natureRef = useRef("off");

  const stopAmbience = useCallback(() => {
    if (ambienceRef.current) {
      ambienceRef.current.stop();
      ambienceRef.current = null;
    }
  }, []);

  const startAmbience = useCallback(
    (type) => {
      stopAmbience();
      if (type === "off") return;
      const ctx = getAudioContext();
      if (!ctx) return;
      const amb = new Ambience(ctx, type);
      amb.start(0.22);
      ambienceRef.current = amb;
    },
    [stopAmbience]
  );

  const chooseNature = (id) => {
    setNature(id);
    natureRef.current = id;
    if (playing) startAmbience(id);
    else if (id === "off") stopAmbience();
  };

  // Pick a pleasant English voice when available
  useEffect(() => {
    if (!supported) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      const preferred =
        voices.find((v) => /female/i.test(v.name) && /en/i.test(v.lang)) ||
        voices.find((v) => /(Samantha|Victoria|Karen|Moira|Tessa|Google US English)/i.test(v.name)) ||
        voices.find((v) => /en-US/i.test(v.lang)) ||
        voices.find((v) => /^en/i.test(v.lang)) ||
        voices[0];
      voiceRef.current = preferred;
    };
    pick();
    window.speechSynthesis.onvoiceschanged = pick;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [supported]);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    if (supported) window.speechSynthesis.cancel();
    stopAmbience();
    setPlaying(false);
    setCurrent(-1);
    idxRef.current = 0;
  }, [supported, stopAmbience]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
      stopAmbience();
    };
  }, [supported, stopAmbience]);

  const speakFrom = useCallback(
    (startIdx) => {
      if (!supported) return;
      cancelledRef.current = false;
      const speakLine = (i) => {
        if (cancelledRef.current) return;
        if (i >= lines.length) {
          setPlaying(false);
          setCurrent(-1);
          idxRef.current = 0;
          return;
        }
        idxRef.current = i;
        setCurrent(i);
        const u = new SpeechSynthesisUtterance(lines[i]);
        if (voiceRef.current) u.voice = voiceRef.current;
        u.rate = 0.82; // slow and calming
        u.pitch = 0.95;
        u.volume = 1;
        u.onend = () => {
          if (cancelledRef.current) return;
          // gentle pause between lines
          setTimeout(() => speakLine(i + 1), 900);
        };
        u.onerror = () => {
          if (!cancelledRef.current) setTimeout(() => speakLine(i + 1), 400);
        };
        window.speechSynthesis.speak(u);
      };
      speakLine(startIdx);
    },
    [lines, supported]
  );

  const togglePlay = () => {
    if (!supported) return;
    if (playing) {
      // pause
      window.speechSynthesis.cancel();
      cancelledRef.current = true;
      stopAmbience();
      setPlaying(false);
    } else {
      setPlaying(true);
      if (natureRef.current !== "off") startAmbience(natureRef.current);
      const resumeIdx = current >= 0 ? current : 0;
      speakFrom(resumeIdx);
    }
  };

  const restart = () => {
    stop();
    setTimeout(() => {
      setPlaying(true);
      if (natureRef.current !== "off") startAmbience(natureRef.current);
      speakFrom(0);
    }, 150);
  };

  return (
    <div className="rounded-2xl border border-neutral-100 bg-gradient-to-br from-[#eef1fb] to-white p-6 md:p-7 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-11 h-11 rounded-full bg-[#5a67a8] text-white flex items-center justify-center flex-shrink-0">
          <Headphones size={20} />
        </span>
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase text-[#5a67a8] font-semibold">
            Guided Meditation
          </p>
          <p className="font-serif-brand text-xl text-purple leading-tight">{label}</p>
        </div>
      </div>

      {supported ? (
        <>
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5a67a8] text-white text-sm font-semibold tracking-wide-sm hover:bg-[#4b578f] transition-colors"
            >
              {playing ? <Pause size={17} /> : <Play size={17} />}
              {playing ? "Pause" : current >= 0 ? "Resume" : "Begin practice"}
            </button>
            {current >= 0 && (
              <button
                onClick={restart}
                aria-label="Restart"
                title="Restart"
                className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-[#5a67a8] hover:border-[#5a67a8] transition-colors"
              >
                <RotateCcw size={16} />
              </button>
            )}
            {playing && (
              <span className="flex items-center gap-1 text-[#5a67a8] text-xs font-medium">
                <Volume2 size={15} /> Speaking...
              </span>
            )}
          </div>

          {/* Nature ambience selector */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] tracking-[0.18em] uppercase text-neutral-500 mr-1">
              Background
            </span>
            {NATURE.map((n) => (
              <button
                key={n.id}
                onClick={() => chooseNature(n.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide-sm transition-all border ${
                  nature === n.id
                    ? "bg-[#5a67a8] text-white border-[#5a67a8]"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-[#5a67a8]"
                }`}
              >
                <n.icon size={14} /> {n.label}
              </button>
            ))}
          </div>

          {/* Script with active line highlighting */}
          <div className="mt-5 space-y-2">
            {lines.map((line, i) => (
              <p
                key={i}
                className={`text-[0.95rem] leading-relaxed transition-colors duration-300 ${
                  i === current
                    ? "text-[#4b578f] font-semibold"
                    : "text-neutral-500"
                }`}
              >
                {line}
              </p>
            ))}
          </div>
          <p className="text-[11px] text-neutral-400 mt-4">
            Audio is generated by your browser's voice. For best results, use headphones in a quiet
            space.
          </p>
        </>
      ) : (
        <div>
          <div className="space-y-2">
            {lines.map((line, i) => (
              <p key={i} className="text-[0.95rem] leading-relaxed text-neutral-600">
                {line}
              </p>
            ))}
          </div>
          <p className="text-[11px] text-neutral-400 mt-4">
            Your browser doesn't support in app audio playback, you can follow the written guidance
            above instead.
          </p>
        </div>
      )}
    </div>
  );
}
